import React from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '../ui';

/**
 * PDF Export Component
 * 
 * Provides functionality to export chat calculations to PDF
 * Only available for signed-in users
 */

// Format chat message for PDF display
const formatMessageForPDF = (content) => {
  return content
    // Convert markdown-style formatting
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
};

// Generate PDF content HTML
const generatePDFContent = (messages, title = 'Calculation Results') => {
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto;">
      <div style="text-align: center; margin-bottom: 40px; border-bottom: 2px solid #3B82F6; padding-bottom: 20px;">
        <h1 style="color: #1F2937; font-size: 24px; margin: 0;">
          <span style="color: #3B82F6;">CalcBuild</span>AI
        </h1>
        <p style="color: #6B7280; margin: 10px 0 0;">Construction Material Calculator</p>
      </div>
      
      <div style="margin-bottom: 30px;">
        <h2 style="color: #1F2937; font-size: 18px; margin: 0 0 5px;">${title}</h2>
        <p style="color: #6B7280; margin: 0; font-size: 14px;">Generated on ${date}</p>
      </div>
      
      <div style="background: #F9FAFB; border-radius: 8px; padding: 20px;">
        ${messages.map((msg, idx) => `
          <div style="margin-bottom: 20px; ${idx < messages.length - 1 ? 'border-bottom: 1px solid #E5E7EB; padding-bottom: 20px;' : ''}">
            <div style="font-weight: 600; color: ${msg.role === 'user' ? '#3B82F6' : '#059669'}; margin-bottom: 8px; font-size: 12px; text-transform: uppercase;">
              ${msg.role === 'user' ? '📝 Your Request' : '🤖 CalcBuildAI'}
            </div>
            <div style="color: #374151; line-height: 1.6; font-size: 14px;">
              ${formatMessageForPDF(msg.content)}
            </div>
          </div>
        `).join('')}
      </div>
      
      <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E7EB; text-align: center; color: #9CA3AF; font-size: 12px;">
        <p>Generated by CalcBuildAI • calcbuildai.com</p>
        <p>This estimate is for planning purposes only. Always verify with local suppliers.</p>
      </div>
    </div>
  `;
};

// Export a single calculation (last AI response)
export const ExportSingleCalculation = ({ messages, materialType, isLoading = false, onExport }) => {
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    if (exporting || isLoading) return;
    
    setExporting(true);
    try {
      // Dynamically import html2pdf to reduce bundle size
      const html2pdf = (await import('html2pdf.js')).default;
      
      // Get the last calculation (last user message + last AI response)
      const lastUserIdx = messages.map(m => m.role).lastIndexOf('user');
      const lastAssistantIdx = messages.map(m => m.role).lastIndexOf('assistant');
      
      if (lastUserIdx === -1 || lastAssistantIdx === -1) {
        throw new Error('No calculation to export');
      }
      
      const exportMessages = [
        messages[lastUserIdx],
        messages[lastAssistantIdx],
      ];
      
      const content = generatePDFContent(exportMessages, `${materialType?.charAt(0).toUpperCase() + materialType?.slice(1) || 'Material'} Calculation`);
      
      const element = document.createElement('div');
      element.innerHTML = content;
      
      await html2pdf()
        .set({
          margin: 10,
          filename: `calcbuildai-${materialType || 'calculation'}-${Date.now()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .save();
      
      if (onExport) onExport();
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      variant="ghost"
      size="sm"
      disabled={exporting || isLoading || messages.length < 2}
      icon={exporting ? Loader2 : Download}
      className={exporting ? 'animate-spin' : ''}
    >
      {exporting ? 'Exporting...' : 'Download PDF'}
    </Button>
  );
};

// Export full chat conversation
export const ExportFullChat = ({ messages, materialType, isLoading = false, onExport }) => {
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    if (exporting || isLoading) return;
    
    setExporting(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      
      if (!messages || messages.length === 0) {
        throw new Error('No messages to export');
      }
      
      const content = generatePDFContent(messages, `${materialType?.charAt(0).toUpperCase() + materialType?.slice(1) || 'Material'} Calculation - Full Conversation`);
      
      const element = document.createElement('div');
      element.innerHTML = content;
      
      await html2pdf()
        .set({
          margin: 10,
          filename: `calcbuildai-full-${materialType || 'chat'}-${Date.now()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .save();
      
      if (onExport) onExport();
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      variant="ghost"
      size="sm"
      disabled={exporting || isLoading || !messages || messages.length === 0}
      icon={exporting ? Loader2 : FileText}
    >
      {exporting ? 'Exporting...' : 'Download Full Chat'}
    </Button>
  );
};

// Combined export dropdown/menu
const ExportPDF = ({ messages, materialType, isLoading = false }) => {
  return (
    <div className="flex items-center gap-2">
      <ExportSingleCalculation 
        messages={messages} 
        materialType={materialType}
        isLoading={isLoading}
      />
      {messages && messages.length > 2 && (
        <ExportFullChat 
          messages={messages} 
          materialType={materialType}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ExportPDF;
